// src/utils/sourceAuthority.js
//
// Pass 82D — WINTER SOURCE AUTHORITY (field/source-specific). Authority is NOT one blanket "the spreadsheet is the
// source of truth" rule (§4). The accepted architecture already separates COMMERCIAL SOURCE AUTHORITY (the SAS master
// booking schedule owns the sold/scheduled commercial facts) from OPERATIONAL ALLOCATION AUTHORITY (the schedule grid
// is assignment EVIDENCE, and MANUAL ASSIGNMENT ALWAYS WINS). This module encodes that as pure predicates so no edit
// path can silently create a SECOND truth that a later sync will overwrite (§5/§6).
//
// PURE (no Firestore, no React). The UI + the writers both consult it so the gate is consistent.

// Canonical source ids (mirror the sheetSync adapters + config; kept local so this leaf module has no heavy imports).
export const SOURCE_IDS = {
  SAS_MASTER: 'sas_master_schedule',            // commercial booking authority (bookingDataMaster EXTERNAL_SOURCE)
  SAS_MASTER_STAFF: 'sas_master_schedule_staff', // RDO/staff activity (operational)
  SAS_GRID: 'sas_schedule_grid',                // operational allocation / assignment evidence
  SAS_COLLECTIVE: 'sas_collective_schedule',    // Collective operational allocation only
  MANUAL: 'manual',                             // Motus-native
};

// A source that owns COMMERCIAL booking truth (sold/scheduled). Editing its fields in Motus would diverge from the
// authoritative spreadsheet until the next sync overwrites them — forbidden (§5).
export const COMMERCIAL_SOURCE_IDS = new Set([SOURCE_IDS.SAS_MASTER]);

// Manual/operator origin — a Motus-NATIVE record (§7). These are NOT a "spreadsheet discrepancy".
export const MANUAL_SOURCE_IDS = new Set(['manual', 'manual_sas', 'app', 'operator', 'ui', 'admin']);

// Field-level authority classes (§4). Every source-backed booking field falls into exactly one.
export const FIELD_AUTHORITY = {
  SOURCE_OWNED: 'source_owned',           // authoritative source owns it → READ-ONLY in Motus for a source-backed record
  MOTUS_ALLOCATION: 'motus_allocation',   // operational allocation; MANUAL ASSIGNMENT ALWAYS WINS → Motus may edit
  MOTUS_AUGMENTATION: 'motus_augmentation', // Motus-only annotation the source never carries → Motus may edit
  MOTUS_NATIVE: 'motus_native',           // the whole record is Motus-native → Motus owns every field
};

// The §4 authority matrix for a SOURCE-BACKED commercial booking. (A Motus-native booking is MOTUS_NATIVE for all.)
export const SOURCE_OWNED_FIELDS = new Set([
  'serviceDate', 'startAt', 'endAt', 'durationCategory', 'duration',
  'requesterName', 'leadGuest', 'clientId', 'requesterEmail', 'requesterPhone',
  'bookingType', 'serviceType', 'discipline', 'skillLevel', 'groupSize',
  'stage', 'status', 'paymentState', 'priceSnapshot', 'price', 'currency',
  'locationLabel', 'meetingType', 'transportInfo', 'pickup', 'accommodationName', 'accommodationAddress',
  'sourceInstructorLabel', 'cancellation', 'cancellationResolution',
]);
// Assignment is OPERATIONAL ALLOCATION — manual always wins, so Motus MAY (re)assign a source-backed booking.
export const MOTUS_ALLOCATION_FIELDS = new Set(['instructorId', 'instructorAuthUserId', 'instructorNameSnapshot', 'assignmentState', 'assignmentSource']);
// Motus-only annotations the source schedule never carries — always editable, never a divergence.
export const MOTUS_AUGMENTATION_FIELDS = new Set(['internalNotes', 'notes', 'operationalNotes', 'opsNotes', 'flag', 'followUpRecommended']);

function sourceIdOf(booking = {}) {
  return String(booking.externalSource || '').trim();
}

// A booking whose COMMERCIAL facts are owned by an external authoritative source (SAS master booking schedule) — or a
// legacy workbook-imported client booking that predates per-source stamping. Its source-owned fields are read-only.
export function isSourceBackedCommercial(booking = {}) {
  const src = sourceIdOf(booking);
  if (COMMERCIAL_SOURCE_IDS.has(src)) return true;
  // legacy workbook imports (bootstrap / imported client bookings) carry commercial truth from the SAS workbook
  if ((booking.importedFromWorkbook || booking.bootstrapImport) && !isManualNative(booking)) return true;
  return false;
}

// A Motus-NATIVE record (operator-created; the source spreadsheet does not own it). §7.
export function isManualNative(booking = {}) {
  const src = String(booking.source || booking.sourceType || '').trim();
  if (MANUAL_SOURCE_IDS.has(src)) return true;
  // no external source id AND not workbook-imported → treat as Motus-native (e.g. app-created)
  if (!sourceIdOf(booking) && !booking.importedFromWorkbook && !booking.bootstrapImport) {
    return src ? MANUAL_SOURCE_IDS.has(src) || src === '' : true;
  }
  return false;
}

// An operational-allocation HOLD (schedule grid / collective) — evidence that blocks availability, never commercial.
export function isOperationalHold(booking = {}) {
  const src = sourceIdOf(booking);
  return src === SOURCE_IDS.SAS_GRID || src === SOURCE_IDS.SAS_COLLECTIVE || booking.activityType === 'client_hold';
}

// The authority class of a single field on a booking.
export function fieldAuthority(booking = {}, field = '') {
  if (isManualNative(booking) && !isSourceBackedCommercial(booking)) return FIELD_AUTHORITY.MOTUS_NATIVE;
  if (MOTUS_ALLOCATION_FIELDS.has(field)) return FIELD_AUTHORITY.MOTUS_ALLOCATION;
  if (MOTUS_AUGMENTATION_FIELDS.has(field)) return FIELD_AUTHORITY.MOTUS_AUGMENTATION;
  if (SOURCE_OWNED_FIELDS.has(field) && isSourceBackedCommercial(booking)) return FIELD_AUTHORITY.SOURCE_OWNED;
  // unknown field on a source-backed record is treated conservatively as source-owned (fail safe, never silent divergence)
  return isSourceBackedCommercial(booking) ? FIELD_AUTHORITY.SOURCE_OWNED : FIELD_AUTHORITY.MOTUS_NATIVE;
}

// May Motus write this field on this booking without risking a silent divergence?
export function canMotusEditField(booking = {}, field = '') {
  return fieldAuthority(booking, field) !== FIELD_AUTHORITY.SOURCE_OWNED;
}

// May Motus edit the commercial/schedule facts (date/time/guest/type/payment/price/pickup) of this booking?
export function canMotusEditCommercialFields(booking = {}) {
  return !isSourceBackedCommercial(booking);
}

// May Motus CANCEL this booking? A source-backed commercial cancellation must happen in the authoritative source
// (cancelling only in Motus would show instructors a cancelled lesson the source still says is live — a divergence).
export function canMotusCancel(booking = {}) {
  return !isSourceBackedCommercial(booking);
}

// May Motus (re)assign an instructor to this booking? YES even for a source-backed booking — assignment is operational
// allocation and MANUAL ASSIGNMENT ALWAYS WINS (accepted invariant). A Motus-native booking is likewise assignable.
export function canMotusAssign(booking = {}) {
  return true;
}

// Short product copy for a blocked source-owned edit (§6) — never raw ids.
export const SOURCE_OWNED_EDIT_MESSAGE = 'Managed by the SAS booking schedule. Update the source schedule, then sync.';

// ── Org-level commercial authority (§8) ────────────────────────────────────────────────────────────────────────────
// Which organizations have an EXTERNALLY-authoritative commercial booking source for a season. For such an org a
// Motus-created client lesson must NOT quietly become a second confirmed commercial truth — the New-booking flow
// records an ENQUIRY (not an instructor-confirmed booking) and directs staff to the authoritative source. A
// Motus-authoritative tenant (e.g. motus-pilot-2) is absent here and keeps native confirmed booking creation.
const COMMERCIAL_AUTHORITY_REGISTRY = {
  'sas-niseko': { '2026_27': SOURCE_IDS.SAS_MASTER },
};

export function commercialSourceAuthorityFor(organizationId = '', seasonId = '') {
  if (!organizationId) return null; // fail closed — never assume SAS
  const byOrg = COMMERCIAL_AUTHORITY_REGISTRY[organizationId];
  if (!byOrg) return null;
  if (seasonId) return byOrg[seasonId] || null;
  return Object.values(byOrg)[0] || null;
}

export function orgHasCommercialSourceAuthority(organizationId = '', seasonId = '') {
  return !!commercialSourceAuthorityFor(organizationId, seasonId);
}

// The human label of the org's authoritative commercial source (for product copy), or ''.
export function commercialAuthorityLabel(organizationId = '', seasonId = '') {
  return commercialSourceAuthorityFor(organizationId, seasonId) ? 'SAS booking schedule' : '';
}
